import * as React from 'react';

import {SessionContent} from 'shells/timeline/entry/content/session/session-content';
import type {Page} from 'shells/timeline/entry/content/session/session-content';
import {SessionMetadata} from 'shells/timeline/entry/content/session/session-metadata';

import type {EventFragmentFull as Event} from 'nutshell-graphql-types';

import {
    getEntryTypeFromPayload,
    getSessionDeviceTypeFromSessionBrowser,
    getActorForSession,
} from '../helpers';

import {TimelineEntryModal} from './timeline-entry-modal';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    event: Event;
    isLoading?: boolean;
};

export function TimelineSessionModal(props: Props) {
    const {isOpen, onClose, isLoading, event} = props;
    const {payload} = event;

    /**
     * We need to include this check, even though we are only passing an event
     * with a payload.__typename of 'Session' because Apollo doesn't generate a specific
     * enough type when using a union type (like the TimelineEventPayload).
     * See this issue for more detail:
     * https://github.com/apollographql/apollo-tooling/issues/1223
     */
    if (payload.__typename === 'Session') {
        const totalDuration = payload.visits.totalDuration || 0;

        const pages: Page[] = payload.visits.edges.map((edge) => ({
            id: edge.node.id,
            durationSeconds: edge.node.durationSeconds ? edge.node.durationSeconds : 0,
            path: edge.node.url.path,
            fullyQualifiedUrl: edge.node.url.fullyQualifiedUrl,
            pageTitle: edge.node.url.pageTitle,
            maUrl: edge.node.maUrl,
        }));

        return (
            <TimelineEntryModal
                isOpen={isOpen}
                onClose={onClose}
                isLoading={isLoading}
                changeLogId={event.id}
                iconVariant='session'
                entryType={getEntryTypeFromPayload(payload)}
                changeType={event.changeType}
                timestamp={event.changeTime}
                actor={getActorForSession(payload, event.actor)}
                entity={payload.url}
                numVisits={payload.visits.totalCount}
                subject={`Via ${payload.source.source}`}
                status={{primary: {level: 'info--grey', text: 'Site visit'}}}
                content={
                    <SessionContent
                        durationSeconds={totalDuration}
                        pages={pages}
                        source={{
                            medium: payload.source.medium,
                            source: payload.source.source,
                        }}
                    />
                }
                maxHeight={475}
                metadata={
                    <SessionMetadata
                        locationSummary={payload.locationSummary}
                        deviceType={getSessionDeviceTypeFromSessionBrowser(payload.browser)}
                        platformType={
                            payload.browser && payload.browser.platform
                                ? payload.browser.platform
                                : undefined
                        }
                    />
                }
                disableReactions={true}
                disableComments={true}
            />
        );
    }

    return null;
}
